import { useMemo, useState } from "react";
import { Area, CartesianGrid, ComposedChart, Line, ReferenceArea, XAxis, YAxis } from "recharts";
import { format, parseISO } from "date-fns";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import type { WasteFilters } from "@/hooks/use-waste-filters";
import type { Lens } from "@/hooks/use-lens";
import { useWasteStats, type WasteStatsBucket } from "@/hooks/use-waste-stats";
import { useDashboardWeather } from "@/hooks/use-dashboard-weather";
import { aggregateWeekly, aggregateMonthly } from "@/lib/waste-report-metrics";
import { useTranslation } from "@/i18n/use-translation";
import { formatCurrency, formatWeight } from "@/lib/format";
import {
  trendWindow,
  trendGhostWindow,
  targetForUnit,
  weatherEmoji,
  densifyDays,
  weekendSpans,
  DAILY_WINDOW_DAYS,
  type Granularity,
} from "@/lib/trend-window";
import { shiftDay, zonedDay } from "@/lib/zoned-day";
import { exploreHref, MEASURE_FOR_LENS } from "@/lib/explore-link";

import { ChartCard } from "./chart-card";
import { SegmentedToggle } from "./segmented-toggle";
import { formatPercent } from "@/lib/format";

const AREA_COLOR = "#2563eb";
const GHOST_COLOR = "#cbd5e1";
const TARGET_COLOR = "#111827";

/** Literal keys, resolved at render — a module-level constant that called `t()` would capture the
 * locale at import (POS2-1134 CP3a). Recharts reads `label` off this, so it is built per render. */
const CONFIG_KEYS = {
  value: { labelKey: "waste.thisPeriod", color: AREA_COLOR },
  ghost: { labelKey: "waste.previousPeriod", color: GHOST_COLOR },
  target: { labelKey: "waste.target", color: TARGET_COLOR },
};

/** This chart's granularity in Explore's vocabulary (POS2-886). */
const EXPLORE_GRAN: Record<Granularity, "daily" | "weekly" | "monthly"> = {
  day: "daily",
  week: "weekly",
  month: "monthly",
};

const CAPTION_KEYS: Record<Granularity, string> = {
  day: "waste.trendCaptionDay",
  week: "waste.trendCaptionWeek",
  month: "waste.trendCaptionMonth",
};

const GRAN_LABEL_KEYS: Record<Granularity, string> = {
  day: "waste.granDaily",
  week: "waste.granWeekly",
  month: "waste.granMonthly",
};

/**
 * Homepage "Waste over time" chart (POS2-870, dashboard-only). Each granularity plots
 * its own fixed window — Daily (2 weeks) adds a previous-period ghost line, weekend
 * shading, and a weather-emoji row; every granularity draws a dashed target line
 * (waste_pct_target × food spend, cost lens only). The global £/kg lens drives units.
 */
export function TrendArea({
  filters,
  currency,
  timezone,
  lens,
  targetPct,
  weeklySpend,
}: {
  /** Resolved single-site stats filters (site_id, status, tier, …). */
  filters: WasteFilters;
  /** Resolved org→account→site→kitchen currency (never hardcoded). */
  currency: string;
  /** Resolved site timezone — the zone the RPC buckets in, so the windows are built in it. */
  timezone: string;
  lens: Lens;
  /** Resolved `waste_pct_target` (%), or null when unset — target line omitted. */
  targetPct: number | null;
  /** Food spend normalised to a week, for the target line; null → no target line. */
  weeklySpend: number | null;
}) {
  const { locale, t } = useTranslation();
  // Rebuilt per render: Recharts reads `label` off this, and a module-level object would hold
  // whichever locale was active at import (POS2-1134 CP5).
  const config: ChartConfig = Object.fromEntries(
    Object.entries(CONFIG_KEYS).map(([key, { labelKey, color }]) => [
      key,
      { label: t(labelKey), color },
    ]),
  );
  const [granularity, setGranularity] = useState<Granularity>("day");
  const now = useMemo(() => new Date(), []);
  const isDaily = granularity === "day";

  const window = useMemo(
    () => trendWindow(granularity, now, timezone),
    [granularity, now, timezone],
  );
  // Food-only universe (POS2-861): packaging is excluded at the RPC — the day
  // dim has no waste-type split, so it can't be subtracted client-side.
  // Only the day dim is read here, so request just that (POS2-960): the 12-month
  // Monthly window is one aggregate pass with no joins instead of all 8 dims.
  const mainQuery = useWasteStats(filters, window, {
    excludeWasteType: "packaging",
    dims: ["day"],
  });
  const ghostWindow = useMemo(() => trendGhostWindow(now, timezone), [now, timezone]);
  // Sequenced after the main query resolves (POS2-994): never run two stats scans
  // concurrently on the shared instance. The ghost is a non-critical Daily-only overlay.
  const ghostQuery = useWasteStats(filters, ghostWindow, {
    enabled: isDaily && mainQuery.isSuccess,
    excludeWasteType: "packaging",
    dims: ["day"],
  });

  // The first site-local day of the Daily window — the anchor for zero-filling to a fixed
  // 14-column axis so weekend bands, the weather row, and the ghost line align by index.
  // Site-local, because that is the day the RPC keys its buckets by (POS2-1039).
  const dailyStartDay = useMemo(
    () => shiftDay(zonedDay(now, timezone), -(DAILY_WINDOW_DAYS - 1)),
    [now, timezone],
  );

  const byDay = useMemo(() => mainQuery.data?.by_day ?? [], [mainQuery.data]);
  const series = useMemo<WasteStatsBucket[]>(() => {
    if (granularity === "week") return aggregateWeekly(byDay);
    if (granularity === "month") return aggregateMonthly(byDay);
    return densifyDays(byDay, dailyStartDay, DAILY_WINDOW_DAYS);
  }, [granularity, byDay, dailyStartDay]);

  const ghost = useMemo<WasteStatsBucket[] | null>(() => {
    if (!isDaily || !ghostQuery.data) return null;
    const ghostStartDay = shiftDay(dailyStartDay, -DAILY_WINDOW_DAYS);
    return densifyDays(ghostQuery.data.by_day, ghostStartDay, DAILY_WINDOW_DAYS);
  }, [isDaily, ghostQuery.data, dailyStartDay]);

  const weatherRange = useMemo(
    () => (isDaily ? { from: window.from, to: now.toISOString() } : { from: null, to: null }),
    [isDaily, window.from, now],
  );
  const weatherQuery = useDashboardWeather(filters.site_id, weatherRange);
  const weatherByKey = useMemo(() => {
    if (!isDaily || !weatherQuery.data) return null;
    const m = new Map<string, string>();
    for (const day of weatherQuery.data.by_day) {
      const emoji = weatherEmoji(day);
      if (emoji) m.set(day.date, emoji);
    }
    return m.size ? m : null;
  }, [isDaily, weatherQuery.data]);

  // "Explore this" (POS2-886): the same question on the free canvas — this chart's own
  // granularity and window, no split, carrying the active scope. The window travels as
  // inclusive civil days; `window.to` is the last millisecond of today, so it is already
  // the last included day.
  const exploreTo = exploreHref({
    gran: EXPLORE_GRAN[granularity],
    // Carry the £/kg lens across as Explore's measure, or the canvas answers a different
    // question than the chart the reader just looked at.
    measure: MEASURE_FOR_LENS[lens],
    window: {
      from: zonedDay(new Date(window.from), timezone),
      to: zonedDay(new Date(window.to), timezone),
    },
    filters: {
      site_id: filters.site_id,
      kitchen_id: filters.kitchen_id,
      device_serial: filters.device_serial,
      waste_type: filters.waste_type,
      service_time: filters.service_time,
      food: filters.food,
      time_from: filters.time_from,
      time_to: filters.time_to,
    },
  });

  const metricOf = (b: WasteStatsBucket) => (lens === "weight" ? b.total_weight : b.total_cost);
  const fmt = (v: number) =>
    lens === "weight" ? formatWeight(locale, v) : formatCurrency(locale, v, currency);

  // Target is a money value (% of spend), so it only overlays the cost lens.
  const target = lens === "cost" ? targetForUnit(weeklySpend, targetPct, granularity) : null;

  const data = series.map((b, i) => ({
    key: b.key,
    value: metricOf(b),
    ghost: ghost ? metricOf(ghost[i]) : null,
    target,
  }));
  const total = series.reduce((sum, b) => sum + metricOf(b), 0);
  const weekendBands = isDaily ? weekendSpans(series.map((b) => b.key)) : [];

  const loading = mainQuery.isLoading && mainQuery.data == null;
  const isEmpty = !loading && series.every((b) => b.event_count === 0);

  return (
    <ChartCard
      title={t("waste.trendTitle")}
      description={t(CAPTION_KEYS[granularity])}
      isEmpty={isEmpty}
      exploreHref={exploreTo}
      action={
        // The value and the toggle stack when they cannot share a line: at 390px they
        // measure 267px against a 260px card, so wrapping the header alone is not enough.
        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
          <span
            className="text-sm font-semibold tabular-nums text-gray-900"
            data-testid="trend-total"
          >
            {fmt(total)}
          </span>
          <SegmentedToggle
            ariaLabel={t("waste.trendGranularityToggle")}
            value={granularity}
            onChange={setGranularity}
            options={(["day", "week", "month"] as const).map((g) => ({
              value: g,
              label: t(GRAN_LABEL_KEYS[g]),
            }))}
          />
        </div>
      }
    >
      {loading ? (
        <Skeleton className="h-[240px] w-full" />
      ) : (
        <>
          <ChartContainer config={config} className="h-[280px] w-full">
            <ComposedChart data={data} margin={{ left: 12, right: 12 }}>
              <CartesianGrid vertical={false} />
              {weekendBands.map((band) => (
                <ReferenceArea
                  key={band.x1}
                  x1={band.x1}
                  x2={band.x2}
                  fill="rgba(17,24,39,0.05)"
                  fillOpacity={1}
                  ifOverflow="extendDomain"
                />
              ))}
              {isDaily ? (
                <XAxis
                  dataKey="key"
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  height={58}
                  tickMargin={6}
                  // Inset the first/last points so their emoji/labels don't
                  // half-overflow the plot edge (points otherwise sit on the edge).
                  padding={{ left: 14, right: 14 }}
                  tick={<DayTick weatherByKey={weatherByKey} />}
                />
              ) : (
                <XAxis
                  dataKey="key"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={24}
                  padding={{ left: 14, right: 14 }}
                  tickFormatter={(value: string) => fmtTick(value, granularity)}
                />
              )}
              <YAxis hide domain={[0, "auto"]} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(_, payload) => fmtTip(payload?.[0]?.payload?.key, granularity)}
                    formatter={(value, name) => {
                      const cfg = config[String(name) as keyof typeof config];
                      return (
                        <div className="flex w-full items-center justify-between gap-3">
                          <span className="flex items-center gap-1.5 text-gray-500">
                            <span
                              className="size-2 shrink-0 rounded-[2px]"
                              style={{ background: (cfg?.color as string) ?? "#9ca3af" }}
                            />
                            {cfg?.label ?? String(name)}
                          </span>
                          <span className="font-medium tabular-nums text-gray-900">
                            {value == null ? "—" : fmt(Number(value))}
                          </span>
                        </div>
                      );
                    }}
                  />
                }
              />
              <Area
                dataKey="value"
                type="linear"
                stroke={AREA_COLOR}
                fill={AREA_COLOR}
                fillOpacity={0.15}
                strokeWidth={2}
              />
              {ghost && (
                <Line
                  dataKey="ghost"
                  type="linear"
                  stroke={GHOST_COLOR}
                  strokeWidth={2.5}
                  dot={false}
                  connectNulls
                />
              )}
              {target != null && (
                // Rendered as a data series (not a ReferenceLine) so it shares the
                // area's scale + axis padding exactly and can't overflow the plot.
                <Line
                  dataKey="target"
                  type="linear"
                  stroke={TARGET_COLOR}
                  strokeWidth={1.5}
                  strokeDasharray="5 4"
                  strokeOpacity={0.7}
                  dot={false}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ChartContainer>

          <ChartLegend
            hasGhost={ghost != null}
            hasTarget={target != null}
            targetPct={targetPct}
            hasWeekend={isDaily && weekendBands.length > 0}
          />
        </>
      )}
    </ChartCard>
  );
}

function ChartLegend({
  hasGhost,
  hasTarget,
  targetPct,
  hasWeekend,
}: {
  hasGhost: boolean;
  hasTarget: boolean;
  targetPct: number | null;
  hasWeekend: boolean;
}) {
  const { locale, t } = useTranslation();
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
      <span className="flex items-center gap-1.5">
        <span className="h-0.5 w-4 rounded-full" style={{ background: AREA_COLOR }} />
        {t("waste.thisPeriod")}
      </span>
      {hasGhost && (
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-4 rounded-full" style={{ background: GHOST_COLOR }} />
          {t("waste.previousPeriod")}
        </span>
      )}
      {hasTarget && (
        <span className="flex items-center gap-1.5">
          <span className="h-0 w-4 border-t border-dashed" style={{ borderColor: TARGET_COLOR }} />
          {t("waste.targetLegend", { percent: formatPercent(locale, (targetPct ?? 0) / 100, 0) })}
        </span>
      )}
      {hasWeekend && (
        <span className="flex items-center gap-1.5">
          <span className="size-3 rounded-sm bg-gray-900/10" />
          {t("waste.weekend")}
        </span>
      )}
    </div>
  );
}

/**
 * Daily-axis tick: the weather emoji above a weekday-initial + day-number label,
 * rendered inside the axis so Recharts places each one exactly under its data
 * point (a separate HTML row drifts out of alignment). Emoji only when the day
 * has a reading.
 */
function DayTick({
  x = 0,
  y = 0,
  payload,
  weatherByKey,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  weatherByKey: Map<string, string> | null;
}) {
  if (!payload) return null;
  const d = parseISO(payload.value);
  const emoji = weatherByKey?.get(payload.value);
  // Fixed baselines (independent of the emoji) keep the weekday/day rows aligned
  // across days, and the extra top offset gives the plot some breathing room.
  return (
    <g transform={`translate(${x},${y})`} textAnchor="middle">
      {emoji && (
        <text y={14} fontSize={13} aria-hidden>
          {emoji}
        </text>
      )}
      <text y={30} fontSize={11} fontWeight={600} fill="#6b7280">
        {format(d, "EEEEEE")}
      </text>
      <text y={43} fontSize={11} fill="#374151">
        {format(d, "d")}
      </text>
    </g>
  );
}

// Axis tick label — months read "MMM yyyy"; day/week keys read "d MMM".
function fmtTick(key: string, granularity: Granularity): string {
  const d = parseISO(key);
  return granularity === "month" ? format(d, "MMM yyyy") : format(d, "d MMM");
}

// Tooltip label — months read "MMM yyyy"; day/week keys read "d MMM yyyy".
function fmtTip(key: string | undefined, granularity: Granularity): string {
  if (!key) return "";
  const d = parseISO(key);
  return granularity === "month" ? format(d, "MMM yyyy") : format(d, "d MMM yyyy");
}
